import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card'
import { Button } from '../../../ui/button'
import { Input } from '../../../ui/input'
import { ChevronDown, Trash2, AlertCircle, Plus } from 'lucide-react'
import { URL_TYPE_OPTIONS } from '../../../../lib/constants'
import { cn } from '../../../../lib/utils'
import type { UrlItem } from '../../../../lib/types'
import { getUrlErrorMessage } from '../../../../lib/validators'

interface OverviewUrlsSectionProps {
  localData: {
    urls: UrlItem[]
  }
  errors: Record<string, string>
  openDropdownId: string | null
  setOpenDropdownId: (id: string | null) => void
  updateField: (field: string, value: any) => void
  updateUrlRow: (id: string, field: keyof UrlItem, value: string) => void
  removeUrlRow: (id: string) => void
  addUrlRow: () => void
  setErrors: (
    updater: (prev: Record<string, string>) => Record<string, string>,
  ) => void
  requestChanges?: Record<string, string>
}

export function OverviewUrlsSection({
  localData,
  errors,
  openDropdownId,
  setOpenDropdownId,
  updateField,
  updateUrlRow,
  removeUrlRow,
  addUrlRow,
  setErrors,
  requestChanges,
}: OverviewUrlsSectionProps) {
  const getAvailableTypes = (currentId: string) => {
    const usedTypes = localData.urls
      .filter((url) => url.id !== currentId)
      .map((url) => url.type)
    return URL_TYPE_OPTIONS.filter(
      (option) => !option.singleUse || !usedTypes.includes(option.value as any),
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>URLs</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {localData.urls.map((urlItem) => (
          <div
            key={urlItem.id}
            id={`url-${urlItem.id}`}
            className="flex flex-col md:flex-row gap-2 md:gap-4 items-start md:items-center"
          >
            <div
              id={urlItem.id}
              className="w-full md:w-40 flex-shrink-0 relative"
            >
              <button
                type="button"
                onClick={() => {
                  if (urlItem.type !== 'website' && urlItem.type !== 'x') {
                    setOpenDropdownId(
                      openDropdownId === urlItem.id ? null : urlItem.id,
                    )
                  }
                }}
                disabled={urlItem.type === 'website' || urlItem.type === 'x'}
                className={cn(
                  'w-full h-10 px-3 py-2 bg-white border border-neutral-200 rounded-md text-sm text-left flex items-center justify-between',
                  'focus-visible:outline-none focus-visible:border-neutral-800',
                  openDropdownId === urlItem.id && 'border-neutral-800',
                  urlItem.type === 'website' || urlItem.type === 'x'
                    ? 'bg-neutral-50 cursor-not-allowed'
                    : '',
                )}
                aria-expanded={openDropdownId === urlItem.id}
                aria-haspopup="listbox"
              >
                <span className="text-foreground">
                  {
                    URL_TYPE_OPTIONS.find((opt) => opt.value === urlItem.type)
                      ?.label
                  }
                </span>
                {urlItem.type !== 'website' && urlItem.type !== 'x' && (
                  <ChevronDown
                    className={cn(
                      'w-4 h-4 transition-transform text-neutral-600',
                      openDropdownId === urlItem.id && 'rotate-180',
                    )}
                  />
                )}
              </button>
              {openDropdownId === urlItem.id &&
                urlItem.type !== 'website' &&
                urlItem.type !== 'x' && (
                  <div className="absolute z-10 w-full mt-1 bg-white text-neutral-950 border border-neutral-200 rounded-md shadow-lg max-h-60 overflow-auto">
                    <div role="listbox">
                      {getAvailableTypes(urlItem.id).map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          role="option"
                          aria-selected={urlItem.type === option.value}
                          onClick={() => {
                            const newType = option.value as UrlItem['type']
                            updateUrlRow(urlItem.id, 'type', newType)
                            if (urlItem.type !== newType)
                              updateUrlRow(urlItem.id, 'url', '')
                            setOpenDropdownId(null)
                          }}
                          className={cn(
                            'w-full px-3 py-2 text-left text-sm hover:bg-neutral-50 focus:bg-neutral-50 focus:outline-none',
                            urlItem.type === option.value &&
                              'bg-neutral-100 font-medium',
                          )}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
            </div>

            <div className="flex-1 min-h-[2.5rem]">
              <Input
                value={urlItem.url}
                onChange={(e) =>
                  updateUrlRow(urlItem.id, 'url', e.target.value)
                }
                onBlur={() => {
                  const error = getUrlErrorMessage(urlItem.url, urlItem.type)
                  if (error)
                    setErrors((prev) => ({
                      ...prev,
                      [`url-${urlItem.id}`]: error,
                    }))
                }}
                placeholder={
                  URL_TYPE_OPTIONS.find((opt) => opt.value === urlItem.type)
                    ?.placeholder || 'https://'
                }
                className={cn(
                  'focus:ring-0 placeholder:text-neutral-400',
                  errors[`url-${urlItem.id}`]
                    ? 'border-status-error bg-status-errorBG'
                    : 'focus:border-input',
                )}
                aria-label={`URL for ${
                  URL_TYPE_OPTIONS.find((opt) => opt.value === urlItem.type)
                    ?.label
                }`}
                aria-describedby={
                  errors[`url-${urlItem.id}`]
                    ? `url-${urlItem.id}-error`
                    : undefined
                }
              />
              <div className="flex flex-col gap-1">
                {errors[`url-${urlItem.id}`] && (
                  <p
                    id={`url-${urlItem.id}-error`}
                    className="text-status-error text-xs inline-flex items-center gap-1"
                    role="alert"
                    aria-live="polite"
                  >
                    <AlertCircle className="w-3 h-3" />
                    {errors[`url-${urlItem.id}`]}
                  </p>
                )}
                {requestChanges?.[`url-${urlItem.id}`] && (
                  <p className="text-status-error text-xs inline-flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {requestChanges[`url-${urlItem.id}`]}
                  </p>
                )}
              </div>
            </div>

            {urlItem.type !== 'website' && urlItem.type !== 'x' && (
              <div className="flex-shrink-0">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeUrlRow(urlItem.id)}
                  className="h-10 w-10 p-0 hover:bg-neutral-100"
                  aria-label="Remove link"
                >
                  <Trash2 className="w-4 h-4 text-neutral-500" />
                </Button>
              </div>
            )}
          </div>
        ))}

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addUrlRow}
          disabled={URL_TYPE_OPTIONS.length === 0}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add URL
        </Button>
      </CardContent>
    </Card>
  )
}
